import house from "../assets/images/shutterstock_617467400-min.jpg";
import constants from "../constants/constants";
import { useState } from "react";
import SEO, { pageSEO } from "../components/seo.component";

export default function Evaluation() {
  const [popup, setPopup] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    propertyAddress: "",
    city: "",
    provinceState: "",
    propertySize: "",
    lotSize: "",
    propertyType: "",
    buildingType: "",
    notes: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Form validation function
  const validateForm = () => {
    const errors = {};
    
    // Check required fields
    if (!formData.name.trim()) {
      errors.name = "Name is required";
    }
    
    if (!formData.email.trim()) {
      errors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      errors.email = "Please enter a valid email address";
    }
    
    if (!formData.phone.trim()) {
      errors.phone = "Phone is required";
    }
    
    if (!formData.propertyAddress.trim()) {
      errors.propertyAddress = "Property address is required";
    }
    
    if (!formData.city.trim()) {
      errors.city = "City is required";
    }
    
    if (!formData.provinceState.trim()) {
      errors.provinceState = "Province/State is required";
    }
    
    if (!formData.propertySize.trim()) {
      errors.propertySize = "Property size is required";
    }
    
    if (!formData.lotSize.trim()) {
      errors.lotSize = "Lot size is required";
    }
    
    if (!formData.propertyType.trim()) {
      errors.propertyType = "Property type is required";
    }
    
    if (!formData.buildingType.trim()) {
      errors.buildingType = "Building type is required";
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  async function handleSubmit(event) {
    event.preventDefault();
    
    // Validate form before submission
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);

    const submissionData = {
      ...formData,
      access_key: constants.EMAIL_ACCESS_KEY,
      subject: "New Lead Interested in Real Estate Reports",
    };

    try {
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await res.json();

      if (data?.success) {
        console.log("Success", data);
        setPopup({
          subject: "Thanks for your message!",
          message: "We will get back to you as soon as possible",
        });
        // Clear form after successful submission
        setFormData({
          name: "",
          email: "",
          phone: "",
          propertyAddress: "",
          city: "",
          provinceState: "",
          propertySize: "",
          lotSize: "",
          propertyType: "",
          buildingType: "",
          notes: "",
        });
        setFormErrors({});
      } else {
        setPopup({
          subject: "We encountered an error in sending this message",
          message: "Please try again later",
        });
      }
    } catch (err) {
      console.log(err);
      setPopup({
        subject: "We encountered an error in sending this message",
        message: "Please try again later",
      });
    }
    
    setIsSubmitting(false);
  }

  const seoConfig = pageSEO.evaluation();
  return (
    <>
      <SEO {...seoConfig} />
      <div className="max-w-[1100px] pt-[150px] mx-auto grid md:grid-cols-2 px-6 gap-x-24 gap-y-10 min-h-[70vh] mb-12">
        <div>
          <h1 className="text-4xl text-black libre">Free Home Evaluation</h1>

          <p className="font-semibold text-med-grey my-5">
            Wondering how much your home would sell for in today's market?
            Please complete the form below and I would be happy to send you a
            Free Home Evaluation.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="form-item">
              <label className="form-label">Name*</label>
              <input
                className={`form-input ${formErrors.name ? 'border-red-500' : ''}`}
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder="Full Name"
              />
              {formErrors.name && (
                <p className="text-red-500 text-sm mt-1">{formErrors.name}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Email*</label>
              <input
                className={`form-input ${formErrors.email ? 'border-red-500' : ''}`}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Email Address"
              />
              {formErrors.email && (
                <p className="text-red-500 text-sm mt-1">{formErrors.email}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Phone*</label>
              <input
                className={`form-input ${formErrors.phone ? 'border-red-500' : ''}`}
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                placeholder="Phone Number"
              />
              {formErrors.phone && (
                <p className="text-red-500 text-sm mt-1">{formErrors.phone}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Property Address*</label>
              <input
                className={`form-input ${formErrors.propertyAddress ? 'border-red-500' : ''}`}
                name="propertyAddress"
                value={formData.propertyAddress}
                onChange={handleInputChange}
                placeholder="Address of the Property"
              />
              {formErrors.propertyAddress && (
                <p className="text-red-500 text-sm mt-1">{formErrors.propertyAddress}</p>
              )}
            </div>
            <div className="flex flex-col sm:flex-row gap-x-10">
              <div className="form-item">
                <label className="form-label">City*</label>
                <input
                  className={`form-input ${formErrors.city ? 'border-red-500' : ''}`}
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  placeholder="City of the Property"
                />
                {formErrors.city && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.city}</p>
                )}
              </div>

              <div className="form-item">
                <label className="form-label">Province/State*</label>
                <input
                  className={`form-input ${formErrors.provinceState ? 'border-red-500' : ''}`}
                  name="provinceState"
                  value={formData.provinceState}
                  onChange={handleInputChange}
                  placeholder="Province/State of the Property"
                />
                {formErrors.provinceState && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.provinceState}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-x-10">
              <div className="form-item">
                <label className="form-label">Property Size*</label>
                <input
                  className={`form-input ${formErrors.propertySize ? 'border-red-500' : ''}`}
                  name="propertySize"
                  value={formData.propertySize}
                  onChange={handleInputChange}
                  placeholder="Approx. Square Footage"
                />
                {formErrors.propertySize && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.propertySize}</p>
                )}
              </div>
              <div className="form-item">
                <label className="form-label">Lot Size*</label>
                <input
                  className={`form-input ${formErrors.lotSize ? 'border-red-500' : ''}`}
                  name="lotSize"
                  value={formData.lotSize}
                  onChange={handleInputChange}
                  placeholder="Approx. Square Footage/Acreage"
                />
                {formErrors.lotSize && (
                  <p className="text-red-500 text-sm mt-1">{formErrors.lotSize}</p>
                )}
              </div>
            </div>
            <div className="form-item">
              <label className="form-label">Property Type*</label>
              <select
                className={`form-input ${formErrors.propertyType ? 'border-red-500' : ''}`}
                name="propertyType"
                value={formData.propertyType}
                onChange={handleInputChange}
              >
                <option value="">Please Select</option>
                <option value="Residential">Residential</option>
                <option value="Commercial">Commercial</option>
                <option value="Land">Land</option>
                <option value="Parking">Parking</option>
                <option value="Investment">Investment</option>
                <option value="Agricultural">Agricultural</option>
              </select>
              {formErrors.propertyType && (
                <p className="text-red-500 text-sm mt-1">{formErrors.propertyType}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Building Type*</label>
              <select
                className={`form-input ${formErrors.buildingType ? 'border-red-500' : ''}`}
                name="buildingType"
                value={formData.buildingType}
                onChange={handleInputChange}
              >
                <option value="">Please Select</option>
                <option value="House">House</option>
                <option value="Townhouse">Townhouse</option>
                <option value="Condo">Condo</option>
                <option value="Duplex">Duplex</option>
                <option value="Triplex">Triplex</option>
                <option value="Office">Office</option>
                <option value="Mobile Home">Mobile Home</option>
                <option value="Retail">Retail</option>
              </select>
              {formErrors.buildingType && (
                <p className="text-red-500 text-sm mt-1">{formErrors.buildingType}</p>
              )}
            </div>
            <div className="form-item">
              <label className="form-label">Other Notes</label>
              <textarea
                className="form-input resize-none h-32"
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                placeholder="Please tell me any other information you feel I should know about your property"
              />
            </div>
            <button
              type="submit"
              className="bg-logo-second text-white px-10 py-3 mt-3 rounded hover:bg-logo-main transition-colors"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Submitting...' : 'Submit'}
            </button>
          </form>
        </div>
        <div className="overflow-hidden h-[600px] ">
          <img className="h-full w-full object-cover" src={house} alt="House" />
        </div>
      </div>
      {popup && (
        <>
          <div className="backdrop" />
          <div className="popup">
            <h5 className="font-bold text-black text-lg">{popup.subject}</h5>
            <p className="font-semibold text-med-grey tracking-wide mb-3">
              {popup.message}
            </p>
            <button onClick={() => setPopup(false)} className="btn-tertiary">
              Close
            </button>
          </div>
        </>
      )}
    </>
  );
}
